import React, { useState } from 'react';
import { Activity, Heart, Pill, Calendar, User, Shield, BarChart } from 'lucide-react';
import NavigationHeader from '../components/common/NavigationHeader';

interface ThemeConfig {
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    accent: string;
  };
  className: string;
}

const themes: Record<string, ThemeConfig> = {
  medical: {
    name: "Medical Professional",
    description: "Clean, clinical interface with medical-grade precision",
    colors: {
      primary: "#3b82f6",
      secondary: "#22c55e", 
      background: "#ffffff",
      text: "#111827",
      accent: "#6366f1"
    },
    className: "theme-medical"
  },
  wellness: {
    name: "Wellness Focused",
    description: "Warm, approachable design reducing medical anxiety",
    colors: {
      primary: "#22c55e",
      secondary: "#fb923c",
      background: "#fafaf9",
      text: "#1c1917",
      accent: "#8b5cf6"
    },
    className: "theme-wellness"
  },
  minimalist: {
    name: "Modern Minimalist", 
    description: "Ultra-clean, high-contrast design for clarity",
    colors: {
      primary: "#0f172a",
      secondary: "#0ea5e9",
      background: "#ffffff",
      text: "#0f172a",
      accent: "#6366f1"
    },
    className: "theme-minimalist"
  },
  accessible: {
    name: "Accessibility First",
    description: "Maximum accessibility with enhanced contrast",
    colors: {
      primary: "#0c4a6e",
      secondary: "#dc2626",
      background: "#ffffff", 
      text: "#000000",
      accent: "#059669"
    },
    className: "theme-accessible"
  },
  dark: {
    name: "Dark Mode Professional",
    description: "Professional dark theme for reduced eye strain",
    colors: {
      primary: "#3b82f6",
      secondary: "#10b981",
      background: "#0f1419",
      text: "#f7fafc",
      accent: "#4299e1"
    },
    className: "theme-dark"
  }
};

interface DesignPreviewProps {
  onBackToApp?: () => void;
}

const DesignPreview: React.FC<DesignPreviewProps> = ({ onBackToApp }) => {
  const [selectedTheme, setSelectedTheme] = useState<string>('medical');
  const currentTheme = themes[selectedTheme];

  const sampleHealthData = {
    glucose: 120,
    bloodPressure: { systolic: 120, diastolic: 80 },
    weight: 150,
    heartRate: 72
  };

  const sampleMedications = [
    { name: "Metformin", dosage: "500mg", frequency: "2x daily", status: "taken" },
    { name: "Lisinopril", dosage: "10mg", frequency: "1x daily", status: "due" },
    { name: "Aspirin", dosage: "81mg", frequency: "1x daily", status: "missed" }
  ];

  const PreviewCard: React.FC<{ children: React.ReactNode; title: string }> = ({ children, title }) => (
    <div className={`preview-card ${currentTheme.className}`}>
      <h3 className="preview-card-title">{title}</h3>
      {children}
    </div>
  );

  const MetricCard: React.FC<{ icon: React.ReactNode; label: string; value: string; status?: string }> = 
    ({ icon, label, value, status }) => (
    <div className={`metric-card ${currentTheme.className} ${status ? `status-${status}` : ''}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-content">
        <span className="metric-label">{label}</span>
        <span className="metric-value">{value}</span>
      </div>
    </div>
  );

  const Button: React.FC<{ variant?: 'primary' | 'secondary' | 'danger'; children: React.ReactNode; size?: 'sm' | 'md' | 'lg' }> = 
    ({ variant = 'primary', children, size = 'md' }) => (
    <button className={`btn btn-${variant} btn-${size} ${currentTheme.className}`}>
      {children}
    </button>
  );

  const FormInput: React.FC<{ label: string; type?: string; placeholder?: string }> = 
    ({ label, type = 'text', placeholder }) => (
    <div className={`form-group ${currentTheme.className}`}>
      <label className="form-label">{label}</label>
      <input 
        type={type} 
        placeholder={placeholder}
        className={`form-input ${currentTheme.className}`}
      />
    </div>
  );

  return (
    <div className={`design-preview-container ${currentTheme.className}`}>
      {onBackToApp && (
        <NavigationHeader 
          title="Design System Preview"
          subtitle="Compare different design themes optimized for healthcare applications"
          onBackToApp={onBackToApp}
        />
      )}
      {/* Theme Selector Header */}
      <div className={`theme-selector-header ${currentTheme.className}`}>
        <div className="container">
          {!onBackToApp && (
            <>
              <h1 className="page-title">ZippyHealth.io Design System Preview</h1>
              <p className="page-subtitle">
                Compare different design themes optimized for healthcare applications
              </p>
            </>
          )}
          
          <div className="theme-selector">
            {Object.entries(themes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => setSelectedTheme(key)}
                className={`theme-option ${key === selectedTheme ? 'active' : ''} ${currentTheme.className}`}
              >
                <div className="theme-color-preview">
                  <div 
                    className="color-dot primary"
                    style={{ backgroundColor: theme.colors.primary }}
                  />
                  <div 
                    className="color-dot secondary"
                    style={{ backgroundColor: theme.colors.secondary }}
                  />
                  <div 
                    className="color-dot accent"
                    style={{ backgroundColor: theme.colors.accent }}
                  />
                </div>
                <div className="theme-info">
                  <span className="theme-name">{theme.name}</span>
                  <span className="theme-description">{theme.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Preview Content */}
      <div className={`preview-content ${currentTheme.className}`}>
        <div className="container">
          
          {/* Navigation Preview */}
          <PreviewCard title="Navigation Components">
            <div className={`nav-preview ${currentTheme.className}`}>
              <div className="nav-sidebar">
                <div className="nav-logo">ZippyHealth.io</div>
                <nav className="nav-menu">
                  <a href="#" className="nav-item active">
                    <Activity size={20} />
                    <span>Dashboard</span>
                  </a>
                  <a href="#" className="nav-item">
                    <Heart size={20} />
                    <span>Conditions</span>
                  </a>
                  <a href="#" className="nav-item">
                    <Pill size={20} />
                    <span>Medications</span>
                  </a>
                  <a href="#" className="nav-item">
                    <Calendar size={20} />
                    <span>Appointments</span>
                  </a>
                  <a href="#" className="nav-item">
                    <BarChart size={20} />
                    <span>Analytics</span>
                  </a>
                </nav>
                <div className="nav-user">
                  <User size={20} />
                  <span>John Doe</span>
                </div>
              </div>
            </div>
          </PreviewCard>

          {/* Health Metrics Dashboard */}
          <PreviewCard title="Health Metrics Dashboard">
            <div className="metrics-grid">
              <MetricCard
                icon={<Activity size={24} />}
                label="Blood Glucose"
                value={`${sampleHealthData.glucose} mg/dL`}
                status="normal"
              />
              <MetricCard
                icon={<Heart size={24} />}
                label="Blood Pressure"
                value={`${sampleHealthData.bloodPressure.systolic}/${sampleHealthData.bloodPressure.diastolic}`}
                status="normal"
              />
              <MetricCard
                icon={<Activity size={24} />}
                label="Weight"
                value={`${sampleHealthData.weight} lbs`}
                status="normal"
              />
              <MetricCard
                icon={<Heart size={24} />}
                label="Heart Rate"
                value={`${sampleHealthData.heartRate} bpm`}
                status="normal"
              />
            </div>
          </PreviewCard>

          {/* Medication Tracking */}
          <PreviewCard title="Medication Management">
            <div className="medication-list">
              {sampleMedications.map((med, index) => (
                <div key={index} className={`medication-item ${currentTheme.className} status-${med.status}`}>
                  <div className="medication-info">
                    <h4 className="medication-name">{med.name}</h4>
                    <p className="medication-details">{med.dosage} • {med.frequency}</p>
                  </div>
                  <div className={`medication-status status-${med.status}`}>
                    {med.status === 'taken' && '✓ Taken'}
                    {med.status === 'due' && '⏰ Due'}
                    {med.status === 'missed' && '⚠ Missed'}
                  </div>
                </div>
              ))}
            </div>
          </PreviewCard>

          {/* Forms & Inputs */}
          <PreviewCard title="Form Components">
            <div className="form-preview">
              <div className="form-row">
                <FormInput label="Symptom Name" placeholder="e.g., Headache" />
                <FormInput label="Severity (1-10)" type="number" placeholder="5" />
              </div>
              <div className="form-row">
                <FormInput label="Duration" placeholder="e.g., 2 hours" />
                <FormInput label="Date & Time" type="datetime-local" />
              </div>
              <div className={`form-group ${currentTheme.className}`}>
                <label className="form-label">Additional Notes</label>
                <textarea 
                  className={`form-textarea ${currentTheme.className}`}
                  placeholder="Describe any triggers, location, or additional details..."
                  rows={3}
                />
              </div>
            </div>
          </PreviewCard>

          {/* Buttons & Actions */}
          <PreviewCard title="Buttons & Interactive Elements">
            <div className="buttons-preview">
              <div className="button-group">
                <h4>Primary Actions</h4>
                <div className="button-row">
                  <Button variant="primary" size="lg">Save Entry</Button>
                  <Button variant="primary">Add Medication</Button>
                  <Button variant="primary" size="sm">Quick Log</Button>
                </div>
              </div>
              
              <div className="button-group">
                <h4>Secondary Actions</h4>
                <div className="button-row">
                  <Button variant="secondary" size="lg">View History</Button>
                  <Button variant="secondary">Export Data</Button>
                  <Button variant="secondary" size="sm">Share</Button>
                </div>
              </div>
              
              <div className="button-group">
                <h4>Danger Actions</h4>
                <div className="button-row">
                  <Button variant="danger" size="lg">Delete Account</Button>
                  <Button variant="danger">Remove Entry</Button>
                  <Button variant="danger" size="sm">Clear</Button>
                </div>
              </div>
            </div>
          </PreviewCard>

          {/* Alerts & Notifications */}
          <PreviewCard title="Alerts & Notifications">
            <div className="alerts-preview">
              <div className={`alert alert-success ${currentTheme.className}`}>
                <Shield size={20} />
                <div>
                  <h4>Medication Reminder</h4>
                  <p>It's time to take your Metformin (500mg)</p>
                </div>
              </div>
              
              <div className={`alert alert-warning ${currentTheme.className}`}>
                <Activity size={20} />
                <div>
                  <h4>Unusual Reading</h4>
                  <p>Your blood pressure reading is higher than usual. Consider contacting your healthcare provider.</p>
                </div>
              </div>
              
              <div className={`alert alert-info ${currentTheme.className}`}>
                <Calendar size={20} />
                <div>
                  <h4>Upcoming Appointment</h4>
                  <p>You have a checkup with Dr. Smith tomorrow at 2:00 PM</p>
                </div>
              </div>
            </div>
          </PreviewCard>

          {/* Data Visualization */}
          <PreviewCard title="Data Visualization">
            <div className="chart-preview">
              <div className={`chart-container ${currentTheme.className}`}>
                <h4>Glucose Levels - Last 7 Days</h4>
                <div className="chart-placeholder">
                  <div className="chart-bars">
                    {[110, 125, 130, 115, 120, 135, 120].map((value, index) => (
                      <div
                        key={index}
                        className="chart-bar"
                        style={{ 
                          height: `${(value / 150) * 100}%`,
                          backgroundColor: currentTheme.colors.primary 
                        }}
                      />
                    ))}
                  </div>
                  <div className="chart-labels">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                      <span key={index} className="chart-label">{day}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </PreviewCard>

          {/* Typography & Content */}
          <PreviewCard title="Typography & Content Hierarchy">
            <div className="typography-preview">
              <h1 className={`typography-h1 ${currentTheme.className}`}>Primary Heading (H1)</h1>
              <h2 className={`typography-h2 ${currentTheme.className}`}>Secondary Heading (H2)</h2>
              <h3 className={`typography-h3 ${currentTheme.className}`}>Tertiary Heading (H3)</h3>
              <h4 className={`typography-h4 ${currentTheme.className}`}>Quaternary Heading (H4)</h4>
              
              <p className={`typography-body ${currentTheme.className}`}>
                This is body text that would be used for longer content descriptions, 
                medical information, and general reading material. It should be highly 
                readable and accessible across all devices.
              </p>
              
              <p className={`typography-small ${currentTheme.className}`}>
                This is smaller text used for captions, footnotes, and secondary information.
              </p>
              
              <div className={`typography-code ${currentTheme.className}`}>
                <code>Monospace text for data values: 120 mg/dL</code>
              </div>
            </div>
          </PreviewCard>

        </div>
      </div>
    </div>
  );
};

export default DesignPreview; 
